import { useEffect, useMemo } from "react";

import { connectAppRealtime, type RealtimeInvalidationPayload } from "./appRealtime";

interface UseRealtimeInvalidationOptions {
  topics: string[];
  onInvalidate: (payload: RealtimeInvalidationPayload) => void;
  eventName?: "app:data-invalidated" | "admin:data-invalidated";
  enabled?: boolean;
  accessToken?: string | null;
  requiresAuth?: boolean;
}

export function useRealtimeInvalidation({
  topics,
  onInvalidate,
  eventName = "app:data-invalidated",
  enabled = true,
  accessToken,
  requiresAuth = false
}: UseRealtimeInvalidationOptions) {
  const normalizedTopics = useMemo(
    () => [...new Set(topics.map((topic) => topic.trim()).filter((topic) => topic.length > 0))],
    [topics]
  );

  useEffect(() => {
    if (!enabled || normalizedTopics.length === 0) {
      return;
    }

    let isCancelled = false;
    let cleanupTimer: number | null = null;
    let socket: Awaited<ReturnType<typeof connectAppRealtime>> = null;

    void (async () => {
      socket = await connectAppRealtime({ accessToken, requiresAuth });

      if (!socket || isCancelled) {
        socket?.disconnect();
        return;
      }

      socket.on(eventName, (payload: RealtimeInvalidationPayload) => {
        if (!payload || !Array.isArray(payload.topics)) {
          return;
        }

        const hasMatchingTopic = payload.topics.some((topic) => normalizedTopics.includes(topic));

        if (!hasMatchingTopic) {
          return;
        }

        if (cleanupTimer) {
          window.clearTimeout(cleanupTimer);
        }

        cleanupTimer = window.setTimeout(() => {
          onInvalidate(payload);
        }, 80);
      });
    })();

    return () => {
      isCancelled = true;
      if (cleanupTimer) {
        window.clearTimeout(cleanupTimer);
      }
      socket?.disconnect();
    };
  }, [accessToken, enabled, eventName, normalizedTopics, onInvalidate, requiresAuth]);
}
